import CustomInput from '@/components/common/CustomInput';
import { PageHeader } from '@/components/common/PageHeader';

import { Button } from '@/components/ui/button';
import { ProfileLayout } from '@/layouts/ProfileLayout';
import { useChangePasswordMutation } from '@/store/api/authApi';
import { handleApiError, showToastMessage } from '@/utils/helper';
import { ChangePasswordFormData, changePasswordSchema } from '@/utils/validations';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

const ChangePassword = () => {
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });
  const navigate = useNavigate();
  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      const payload = {
        oldPassword: data.oldPassword.trim(),
        newPassword: data.newPassword.trim(),
      };

      await changePassword(payload).unwrap();
      showToastMessage('success', 'Password changed successfully!');
      reset();
    } catch (err: any) {
      console.error('Failed to change password:', err);
      handleApiError(err?.data?.message || 'Failed to change password. Please try again.');
    }
  };

  const fields = [
    {
      name: 'oldPassword' as const,
      label: 'Old Password',
      placeholder: 'Enter old password',
    },
    {
      name: 'newPassword' as const,
      label: 'New Password',
      placeholder: 'Enter new password',
    },
    {
      name: 'confirmPassword' as const,
      label: 'Confirm Password',
      placeholder: 'Enter confirm password',
    },
  ];

  return (
    <ProfileLayout>
      <div className="pt-2">
        {/* Headind */}
        <PageHeader title="Change Password" />
        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="w-full rounded-2xl gradient-box p-5 flex flex-col gap-2">
            {fields.map((field) => (
              <CustomInput
                key={field.name}
                name={field.name}
                control={control}
                type="password"
                label={field.label}
                placeholder={field.placeholder}
                inputRules={{ noSpace: true, maxLength: 32 }}
              />
            ))}
            <Button
              type="submit"
              className="w-full mt-2  rounded-full text-white text-base"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Change Password'}
            </Button>
          </div>
        </form>
      </div>
    </ProfileLayout>
  );
};

export default ChangePassword;
